import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { engineTrendDataProcess } from "../../../util/trendDataProcess/engineTrendDataProcess";
import { Box, CircularProgress } from "@mui/material";
import AmCharts from "../../chart/amcharts";
import { GRAPH_TYPE } from "../../../constants/trendGraph/trendGraphType";
import { DASHBOARD_ENGINE_TREND_TYPE } from "../constants";
import { createCSV, createCsvDownload } from "../../../helpers/csvDownload/createCSV";
import { getCurrentFormattedDateTimeTextWithTimezoneForCSV } from "../../../util/timezone";
import { useTranslation } from "react-i18next";
import { selectedTimezone } from "../../../model/slice/timezoneSlice";
import { fetchEngineData, fetchMainEngineData } from "../../../api/baseEngineData";
import { fetchVesselGpvtgData } from "../../../api/navigationData";
import { convertPositionTextToNumeric } from "../../../helpers/enginePosition";
import { calDurations } from "../../../constants/calDuration";

const onCSVButtonClickHandler = async ({ graphData, vesselName, chartName, timezone }) => {
  const blob = await createCSV(graphData, ",");
  const fileName = `${vesselName}_engine_${chartName}_${getCurrentFormattedDateTimeTextWithTimezoneForCSV(timezone)}`;
  createCsvDownload({ blob, filename: fileName });
};

export const EngineTrendWidget = (props) => {
  const { vessel, selectedChart, enginePosition, index, startDate, endDate } = props;
  const { t } = useTranslation();
  const timezone = useSelector(selectedTimezone);
  const [graphData, setGraphData] = useState([]);
  const [engineData, setEngineData] = useState([]);
  const [navigationData, setNavigationData] = useState([]);
  const [mainEngineData, setMainEngineData] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const graphName = DASHBOARD_ENGINE_TREND_TYPE(enginePosition).find(
    (e) => e.chartType === selectedChart
  ).name;

  useEffect(() => {
    const fetchData = async ({ vesselId, startDate, endDate, interval }) => {
      const controller = new AbortController();
      const signal = controller.signal;
      if (!enginePosition) return { engineData: [], navigationData: [], mainEngineData: [] };
      setIsFetching(true);
      const [fetchedEngineData, fetchedNavigationData] = await Promise.all([
        fetchEngineData(vesselId, startDate, endDate, interval, signal),
        fetchVesselGpvtgData(vesselId, startDate, endDate, interval, signal),
      ]);
      const fetchedMainEngineData = {};
      const data = await fetchMainEngineData(
        convertPositionTextToNumeric(enginePosition),
        vesselId,
        startDate,
        endDate,
        interval,
        signal
      );
      fetchedMainEngineData[enginePosition] = data;
      setEngineData(fetchedEngineData);
      setNavigationData(fetchedNavigationData);
      setMainEngineData(fetchedMainEngineData);
      setIsFetching(false);
    };
    const interval = calDurations({ startDate, endDate });
    fetchData({ vesselId: vessel.id, startDate, endDate, interval });
  }, [startDate, endDate, vessel.id]);

  useEffect(() => {
    if (isFetching || !mainEngineData) return;
    const dataForProcessing = [engineData, mainEngineData[enginePosition], navigationData];
    engineTrendDataProcess(dataForProcessing, selectedChart, enginePosition, vessel, "", "").then(
      (d) => {
        setGraphData(d);
      }
    );
  }, [isFetching]);
  return (
    <Box sx={{ height: "100%", padding: "8px 16px" }}>
      <h2 style={{ color: "white" }}>{graphName}</h2>
      <button
        name="csvDownloadButton"
        style={{ visibility: "hidden" }}
        onClick={() => {
          onCSVButtonClickHandler({
            graphData,
            vesselName: vessel.name,
            chartName: t(graphName),
            timezone,
          });
        }}
      />
      <Box sx={{ height: "calc(100% - 30px)", minHeight: "100px" }}>
        {isFetching ? (
          <CircularProgress />
        ) : (
          <AmCharts
            graphData={graphData}
            graphDataDigital={[]}
            vesselId={vessel.id}
            chartType={selectedChart}
            type={GRAPH_TYPE.ENGINE}
            isComparison={false}
            vesselData={vessel}
            isMockPreview={false}
            expand={true}
            multipleGraphIndex={index}
            isVesselOverview={false}
            isDashboard={true}
            overviewTrendIdx={0}
          />
        )}
      </Box>
    </Box>
  );
};

EngineTrendWidget.propTypes = {
  vessel: PropTypes.object,
  selectedChart: PropTypes.string,
  enginePosition: PropTypes.string,
  index: PropTypes.number,
  startDate: PropTypes.object,
  endDate: PropTypes.object,
};
